import React, { useEffect, useState, useCallback } from "react";
import { Form, Button, Modal, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getCountries,
  getStates,
  getCities,
  resetLocation,
} from "../../../../store/features/location/locationSlice";

// Reusable Input Field
const FormField = ({
  formik,
  label,
  name,
  type = "text",
  as = "input",
  required = false,
}) => (
  <Form.Group className="mb-2">
    <Form.Label>
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Form.Label>
    <Form.Control
      as={as}
      type={type}
      {...formik.getFieldProps(name)}
      isInvalid={formik.touched[name] && formik.errors[name]}
    />
    <Form.Control.Feedback type="invalid">
      {formik.errors[name]}
    </Form.Control.Feedback>
  </Form.Group>
);

// Reusable Select Field
const SelectField = ({
  formik,
  label,
  name,
  options = [],
  onChange,
  required = false,
}) => (
  <Form.Group className="mb-2">
    <Form.Label>
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Form.Label>
    <Form.Select
      name={name}
      value={formik.values[name]}
      onChange={onChange || formik.handleChange}
      isInvalid={formik.touched[name] && formik.errors[name]}
      disabled={formik.values[name] === "superadmin" ? true : false}
    >
      <option value="">Select {label.toLowerCase()}</option>
      {options.map((opt) => (
        <option key={opt.value || opt} value={opt.value || opt}>
          {opt.label || opt}
        </option>
      ))}
    </Form.Select>
    <Form.Control.Feedback type="invalid">
      {formik.errors[name]}
    </Form.Control.Feedback>
  </Form.Group>
);

// Role Options Based on Current Role
const getRoleOptions = (currentRole) => {
  if (currentRole === "assistant superadmin") return ["manager", "staff"];
  if (currentRole === "manager") return ["staff"];
  return ["assistant superadmin", "manager", "staff", "auditor"];
};

const UserModal = ({ showForm, setShowForm, editingUser, formik }) => {
  const dispatch = useDispatch();
  const { countries, states, cities } = useSelector((state) => state.location);
  const { role } = useSelector((state) => state.auth);

  const [selectedCountry, setSelectedCountry] = useState(
    formik.values.country || ""
  );
  const [selectedState, setSelectedState] = useState(formik.values.state || "");
  const [showStateOther, setShowStateOther] = useState(false);
  const [showCityOther, setShowCityOther] = useState(false);

  // File input handler
  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) formik.setFieldValue(field, file);
  };

  // Handle country select
  const handleCountryChange = useCallback(
    (e) => {
      const country = e.target.value;
      setSelectedCountry(country);
      setSelectedState("");
      dispatch(resetLocation());
      formik.setFieldValue("country", country);
      formik.setFieldValue("state", "");
      formik.setFieldValue("city", "");
      // reset other flags when country changes
      setShowStateOther(false);
      setShowCityOther(false);
    },
    [dispatch, formik]
  );

  // Handle state select
  const handleStateChange = useCallback(
    (e) => {
      const state = e.target.value;
      setSelectedState(state);
      formik.setFieldValue("state", state);
      formik.setFieldValue("city", "");
      // if user selects a normal state, hide city-other
      setShowCityOther(false);
    },
    [formik]
  );

  const handleStateOtherToggle = (e) => {
    const checked = e.target.checked;
    setShowStateOther(checked);
    if (checked) {
      // clear select and focus will be on custom input
      setSelectedState("");
      formik.setFieldValue("state", "");
      // also clear cities when switching to other
      formik.setFieldValue("city", "");
      setShowCityOther(true); // allow city custom by default when state is other
    } else {
      // turning off other, clear custom value
      formik.setFieldValue("state", "");
      setShowCityOther(false);
    }
  };

  const handleCityOtherToggle = (e) => {
    const checked = e.target.checked;
    setShowCityOther(checked);
    if (checked) {
      formik.setFieldValue("city", "");
    }
  };

  const handleStateOtherChange = (e) => {
    const v = e.target.value;
    formik.setFieldValue("state", v);
  };

  const handleCityOtherChange = (e) => {
    const v = e.target.value;
    formik.setFieldValue("city", v);
  };

  // Load countries on mount
  useEffect(() => {
    dispatch(getCountries());
  }, [dispatch]);

  // Load states when country changes
  useEffect(() => {
    const selected = countries.find((c) => c.name === formik.values.country);
    if (selected?.isoCode) dispatch(getStates(selected.isoCode));
  }, [formik.values.country, countries, dispatch]);

  // Load cities when state changes
  useEffect(() => {
    const countryCode = countries.find(
      (c) => c.name === formik.values.country
    )?.isoCode;
    const stateCode = states.find(
      (s) => s.name === formik.values.state
    )?.isoCode;
    if (countryCode && stateCode) {
      dispatch(getCities({ countryCode, stateCode }));
    }
  }, [formik.values.state, formik.values.country, countries, states, dispatch]);

  // If the current state/city value isn't in the fetched lists, show the 'Other' textbox
  useEffect(() => {
    if (formik.values.state) {
      const exists = states.some((s) => s.name === formik.values.state);
      setShowStateOther(!exists);
    }
    if (formik.values.city) {
      const existsCity = cities.some(
        (c) => (c.name || c) === formik.values.city
      );
      setShowCityOther(!existsCity);
    }
  }, [states, cities, formik.values.state, formik.values.city]);

  const roleOptions = getRoleOptions(role).map((r) => ({ label: r, value: r }));

  return (
    <Modal size="lg" show={showForm} onHide={() => setShowForm(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editingUser.name ? "Edit User" : "Add User"}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <FormField
                formik={formik}
                label="Name"
                name="name"
                required={true}
              />
            </Col>

            <Col md={6}>
              <FormField
                formik={formik}
                label="Email"
                name="email"
                required={true}
              />
            </Col>

            <Col md={6}>
              <FormField
                formik={formik}
                label="Phone"
                name="phone"
                required={true}
              />
            </Col>

            <Col md={6}>
              <SelectField
                formik={formik}
                label="Country"
                name="country"
                options={countries.map((c) => ({
                  label: c.name,
                  value: c.name,
                }))}
                onChange={handleCountryChange}
                required={true}
              />
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>
                  State <span style={{ color: "red" }}>*</span>
                </Form.Label>

                {!showStateOther ? (
                  <Form.Select
                    name="state"
                    value={formik.values.state}
                    onChange={handleStateChange}
                    isInvalid={formik.touched.state && formik.errors.state}
                  >
                    <option value="">Select state</option>
                    {states.map((s) => (
                      <option key={s.isoCode || s.name} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    placeholder="Enter state"
                    value={formik.values.state}
                    onChange={handleStateOtherChange}
                    isInvalid={formik.touched.state && formik.errors.state}
                  />
                )}

                <div className="form-check mt-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="stateOther"
                    checked={showStateOther}
                    onChange={handleStateOtherToggle}
                  />
                  <label className="form-check-label ms-2" htmlFor="stateOther">
                    Other state
                  </label>
                </div>

                <Form.Control.Feedback type="invalid">
                  {formik.errors.state}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>
                  City <span style={{ color: "red" }}>*</span>
                </Form.Label>

                {!showCityOther ? (
                  <Form.Select
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    isInvalid={formik.touched.city && formik.errors.city}
                  >
                    <option value="">Select city</option>
                    {cities.map((c, idx) => (
                      <option key={c.name || idx} value={c.name || c}>
                        {c.name || c}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    placeholder="Enter city"
                    value={formik.values.city}
                    onChange={handleCityOtherChange}
                    isInvalid={formik.touched.city && formik.errors.city}
                  />
                )}

                <div className="form-check mt-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="cityOther"
                    checked={showCityOther}
                    onChange={handleCityOtherToggle}
                  />
                  <label className="form-check-label ms-2" htmlFor="cityOther">
                    Other city
                  </label>
                </div>

                <Form.Control.Feedback type="invalid">
                  {formik.errors.city}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <FormField
                formik={formik}
                label="Password"
                name="password"
                type="password"
                required={true}
              />
            </Col>

            <Col md={6}>
              <FormField
                formik={formik}
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                required={true}
              />
            </Col>

            <Col md={6}>
              <FormField
                formik={formik}
                label="Organization"
                name="org"
                required={true}
              />
            </Col>

            <Col md={6}>
              <SelectField
                formik={formik}
                label="Role"
                name="role"
                options={roleOptions}
                required={true}
              />
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Avatar (optional)</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => handleFileChange(e, "avatar")}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Upload CV (optional)</Form.Label>
                <Form.Control
                  type="file"
                  onChange={(e) => handleFileChange(e, "uploadCv")}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {editingUser.name ? "Update" : "Create"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserModal;
